import React, { FC, ReactNode, useEffect, useRef } from 'react';
import { ReactComponent as CloseIcon } from '../../icons/close-modal.svg';
import './organisation-modal.scss';

interface OrganisationModalProps {
  /** Coral glyph above the title */
  icon: ReactNode;
  title: string;
  /** Shown as a pill under the title */
  error?: string | null;
  /** Escape, the scrim and the close button are ignored while busy */
  busy?: boolean;
  testId?: string;
  /** Extra class on the dialog, for one surface's own sizing */
  className?: string;
  onClose: () => void;
  onSubmit: () => void;
  /** Footer actions; the primary one should be `type='submit'` */
  actions: ReactNode;
  children: ReactNode;
}

/** Dream 4.0 settings dialog: icon, display title, white body panel, pill actions. */
const OrganisationModal: FC<OrganisationModalProps> = ({
  icon,
  title,
  error,
  busy = false,
  testId,
  className,
  onClose,
  onSubmit,
  actions,
  children,
}) => {
  const latest = useRef({ busy, onClose });
  latest.current = { busy, onClose };
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || latest.current.busy) return;
      e.stopPropagation();
      latest.current.onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const close = () => {
    if (!busy) onClose();
  };
  const titleId = `${testId ?? 'organisation-modal'}-title`;

  return (
    <div
      className='organisation-modal-overlay'
      onClick={close}
      data-testid={testId}
    >
      <form
        className={`organisation-modal${className ? ` ${className}` : ''}`}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        noValidate
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <div className='organisation-modal-header'>
          <span className='organisation-modal-icon' aria-hidden='true'>
            {icon}
          </span>
          <button
            type='button'
            className='organisation-modal-close'
            aria-label='Close'
            disabled={busy}
            onClick={close}
          >
            <CloseIcon />
          </button>
        </div>
        <h2 className='organisation-modal-title' id={titleId}>
          {title}
        </h2>
        {error && (
          <p className='organisation-modal-error' role='alert'>
            {error}
          </p>
        )}
        {children}
        <div className='organisation-modal-actions'>{actions}</div>
      </form>
    </div>
  );
};

export default OrganisationModal;
